// Blog posts for the Teniqua Systems site. Each post's `body` is an ordered
// list of blocks the BlogPost page renders (h2 headings and paragraphs), so we
// can add posts without touching the rendering code.

const posts = [
  {
    slug: 'we-built-dice-because-we-kept-losing-ours',
    title: 'We built Dice because we kept losing ours',
    date: '2026-07-15',
    author: 'The Teniqua Systems team',
    readingMinutes: 3,
    excerpt:
      'Every board game night ends the same way: the box is open, everyone is ready, and the dice are nowhere to be found. So we made a dice roller that lives on your phone and never rolls under the couch.',
    body: [
      {
        type: 'p',
        text: 'There is a very particular kind of frustration that only board game players know. It is the moment when the snacks are out, the board is set up, everyone has picked a colour, and then someone opens the little bag where the dice are supposed to live and finds it empty. The dice are gone. They rolled off the table three weeks ago, got hoovered up, ended up in a coat pocket, or vanished into the same dimension that eats odd socks. You end up on your hands and knees under the sofa with a torch, and the game has not even started.',
      },
      {
        type: 'p',
        text: 'This happened to us one too many times. So we built Dice — a dice roller that lives on your phone and cannot roll under the couch, get chewed by the dog, or disappear the day before games night.',
      },
      {
        type: 'h2',
        text: 'A proper set of dice, not just a d6',
      },
      {
        type: 'p',
        text: 'Plenty of apps give you a single six-sided die. We wanted the whole set, because half the games we play need more than that. Dice rolls the full polyhedral family — d4, d6, d8, d10, d12 and d20 — so it covers everything from a quick board game to a full tabletop RPG night. And it is not a silent, lifeless number popping onto a screen: there is realistic sound and haptic feedback, so a roll actually feels like a roll.',
      },
      {
        type: 'h2',
        text: 'The bits we added because we play these games too',
      },
      {
        type: 'p',
        text: 'A couple of extras crept in because they scratched our own itches. There is a dedicated Catan Cities & Knights mode with the event die, for when the red and yellow dice are not enough on their own. And there is a Magic 8-Ball, because sometimes the roll you need is not a number at all — it is a verdict on whether to order pizza.',
      },
      {
        type: 'h2',
        text: 'Free, offline, and out of your way',
      },
      {
        type: 'p',
        text: 'Dice follows the same rules as everything else we make. No ads getting between you and the game. No account to create. Install it once and it works offline, so it is there on games night whether or not the wifi is. It is a small thing, but it means you will never again start an evening on the floor looking for a d6. The dice are already in your pocket.',
      },
    ],
  },
  {
    slug: 'introducing-the-body-calculator',
    title: 'Introducing The Body Calculator: your health numbers, worked out on your device',
    date: '2026-07-15',
    author: 'The Teniqua Systems team',
    readingMinutes: 4,
    excerpt:
      'Our second app is live. The Body Calculator is a set of more than thirty research-backed health calculators that run entirely in your browser — nothing you type ever leaves your device.',
    body: [
      {
        type: 'p',
        text: 'Health numbers are everywhere and almost none of them come with context. A doctor rattles off a BMI, a blood-test printout lists a cholesterol ratio and an eGFR, a fitness app quotes a VO2 max — and you are left holding a figure with no sense of whether it is good, borderline or worth a conversation. Most calculators online are no better: they spit out a number, wrap it in three adverts, and quietly ship whatever you typed off to a server. We wanted the opposite of that, so we built it. The Body Calculator is live, and it is the second app to come out of Teniqua Systems.',
      },
      {
        type: 'h2',
        text: 'More than thirty calculators, one honest idea',
      },
      {
        type: 'p',
        text: 'The site collects over thirty calculators into one place, grouped so you can actually find things. There is a cardiovascular set — cholesterol ratio, the Framingham risk score, CHADS-VASc, mean arterial pressure and more. A metabolic group covering HbA1c, HOMA-IR, the TyG index and eGFR and other kidney measures. Weight and body-composition tools: BMI, body fat, ideal body weight, body surface area. A diet section for BMR, TDEE, macros, protein, fibre and calorie targets. Fitness numbers like VO2 max, one-rep max, max heart rate and Karvonen training zones. And a handful of validated screeners — GAD-7, the Epworth sleepiness scale, STOP-BANG for sleep apnoea.',
      },
      {
        type: 'p',
        text: 'The common thread is that each result is shown against its target range, not just as a bare figure. You see where your number sits, what the healthy band looks like, and enough plain-language explanation to know whether it is something to act on. The point is not to replace a clinician — it is to help you walk into that conversation understanding your own numbers.',
      },
      {
        type: 'h2',
        text: 'Research-backed, not made up',
      },
      {
        type: 'p',
        text: 'Anyone can put a formula in a text box. The harder part is using the right formula and being honest about where it comes from and what it is for. The calculators here are built on published clinical equations and scoring systems — the same Framingham, CHADS-VASc, Cockcroft-Gault, GAD-7 and Epworth instruments used in practice — rather than invented rules of thumb. Where a score has limits, we say so, because a number you trust blindly is worse than no number at all.',
      },
      {
        type: 'h2',
        text: 'Everything stays on your device',
      },
      {
        type: 'p',
        text: 'This is the part we care about most, and it is the same principle behind CodePeek. Health information is about as personal as data gets, so the safest way to handle it is to never receive it. Every calculation on The Body Calculator runs in your browser. Your weight, your blood pressure, your test results, your answers to a mental-health screener — none of it is sent to us or to anyone else. There is no account to create and nothing to log in to. Open the page and it works; it is a static site delivered from the edge, with the maths happening entirely on your side of the connection.',
      },
      {
        type: 'h2',
        text: 'The pattern behind our apps',
      },
      {
        type: 'p',
        text: 'CodePeek reads a QR or barcode and keeps the scan on your phone. The Body Calculator works out your health numbers and keeps them on your device too. Different jobs, same rules: do the work locally, explain things in plain language, and never quietly harvest what people hand over. If you have a health number you have never quite understood, go and look it up — and know that this time, the answer stays with you.',
      },
    ],
  },
  {
    slug: 'how-to-spot-a-dodgy-qr-code',
    title: 'How to spot a dodgy QR code before you scan it',
    date: '2026-07-13',
    author: 'The Teniqua Systems team',
    readingMinutes: 5,
    excerpt:
      'QR codes are everywhere now, and scammers have noticed. Here is how the cons actually work, and a few simple habits that keep you out of trouble.',
    body: [
      {
        type: 'p',
        text: 'A QR code is a promise you cannot read. To a human eye it is just a square of noise — you have no idea whether it points to a restaurant menu or a fake login page until your phone has already opened it. That gap between "point camera" and "know where you are going" is exactly the gap scammers have moved into, and it has a name now: quishing, or QR phishing.',
      },
      {
        type: 'p',
        text: 'It is worth understanding how these cons work, because once you see the pattern they are easy to sidestep.',
      },
      {
        type: 'h2',
        text: 'The sticker swap',
      },
      {
        type: 'p',
        text: 'The oldest trick in the physical world is also the simplest. Somewhere a legitimate QR code is doing a real job — a parking meter, a restaurant table, a poster for an event, an electric-vehicle charger. A scammer prints their own code on a sticker and slaps it straight over the top. You scan it, land on a page that looks like the real payment screen, type in your card details, and hand them straight to a stranger.',
      },
      {
        type: 'p',
        text: 'The tell is physical: a code printed on a sticker stuck over another surface, edges peeling, sitting slightly crooked over the original. If a code looks like an afterthought glued onto official signage, be suspicious.',
      },
      {
        type: 'h2',
        text: 'The code in your inbox',
      },
      {
        type: 'p',
        text: 'The other big one arrives by email or as a PDF attachment. A message claiming to be from your bank, a delivery company or your own IT department asks you to "scan this code to verify your account" or "confirm your delivery". Putting the link inside an image is deliberate: it slips past a lot of email security filters that would have flagged a plain dodgy link, and it nudges you onto your phone, where the address bar is small and easy to ignore.',
      },
      {
        type: 'p',
        text: 'Rule of thumb: a real company almost never needs you to scan a QR code out of an email to log in or "verify" anything. If it feels like it is manufacturing urgency, that is the scam talking.',
      },
      {
        type: 'h2',
        text: 'Check the destination, not the pattern',
      },
      {
        type: 'p',
        text: 'You cannot judge a QR code by looking at it, so the whole game is seeing where it leads before you commit. A few habits do most of the work:',
      },
      {
        type: 'p',
        text: 'Use a reader that shows you the link instead of just opening it. The address is where the truth is. Look at the real domain — the actual site name, like yourbank.com — not the long tail of characters after it, which can say anything. Watch for lookalikes that swap or add a character (paypa1.com, your-bank-secure.com) or use letters from another alphabet to imitate a familiar name. Be wary of a plain http:// address asking for anything sensitive, and never type a password or card number into a page you reached by scanning a code you were not expecting.',
      },
      {
        type: 'h2',
        text: 'Where CodePeek fits in',
      },
      {
        type: 'p',
        text: 'This is the exact problem we built CodePeek to help with. Point it at a code and, instead of whisking you off to a website, it reads the code and shows you what is inside. For a link it pulls the real domain out and puts it front and centre, and it warns you when an address is insecure or uses a lookalike, dressed-up domain. Nothing opens automatically. You see where the code wants to send you and you make the call.',
      },
      {
        type: 'p',
        text: 'None of this requires you to become a security expert. It just requires one small pause — checking the destination before you act — and a tool that makes that pause easy. Scan freely; just look before you leap.',
      },
    ],
  },
  {
    slug: 'whats-inside-a-product-barcode',
    title: "What's actually inside a product barcode",
    date: '2026-07-12',
    author: 'The Teniqua Systems team',
    readingMinutes: 5,
    excerpt:
      'That beep at the till hides a surprising amount of logic. Here is what the stripes on a product barcode really encode — and what they do not.',
    body: [
      {
        type: 'p',
        text: 'Every product you buy has a little picket fence of black bars on it, and most of us never give it a second thought beyond the beep at the checkout. But there is a neat bit of design hiding in those stripes, and once you know how to read it you start noticing things — like the fact that the barcode does not actually contain the price.',
      },
      {
        type: 'h2',
        text: 'It is just a number',
      },
      {
        type: 'p',
        text: 'The bars are only a way of writing a number that a scanner can read quickly, from an angle, in bad light. The most common one on groceries worldwide is an EAN-13: thirteen digits, encoded as bars of varying width. The price you pay is not in there at all — the till looks that number up in the shop\'s own database and pulls the current price from it. That is why the same item can be on special one week and full price the next without anyone reprinting a single label.',
      },
      {
        type: 'h2',
        text: 'Breaking down the thirteen digits',
      },
      {
        type: 'p',
        text: 'An EAN-13 splits into a few meaningful parts. The first two or three digits are a GS1 prefix, assigned to the numbering organisation that issued the code. The next block identifies the company that registered the product. The block after that is the specific item — this exact size and flavour. And the very last digit is a check digit, which is where it gets clever.',
      },
      {
        type: 'h2',
        text: 'The self-checking trick',
      },
      {
        type: 'p',
        text: 'That final digit is not part of the product identity at all. It is a checksum, calculated from all the digits before it using a fixed formula (odd and even positions are weighted differently, added up, and turned into a single digit). When a scanner reads the code, it runs the same sum and confirms the last digit matches. If a bar is smudged or misread, the maths will not add up and the scanner simply refuses the read rather than ringing up the wrong item. It is a tiny, elegant guard against error, built into the number itself.',
      },
      {
        type: 'h2',
        text: 'The country-of-origin myth',
      },
      {
        type: 'p',
        text: 'Here is the one that trips almost everyone up. That opening GS1 prefix is often called a "country code", and people assume it tells you where the product was made. It does not. It tells you where the company registered its barcode, which may be nothing to do with where the goods were manufactured. A product made in one country by a company registered in another will carry the prefix of the registration, not the factory. If you want to know where something was made, read the label — not the barcode.',
      },
      {
        type: 'h2',
        text: 'UPC, ISBN and the rest of the family',
      },
      {
        type: 'p',
        text: 'EAN-13 has close relatives. In North America you will see a lot of UPC-A, a twelve-digit version that works on the same principles. Books use a special case: an EAN-13 starting with 978 or 979 is really an ISBN, the international book number, in barcode clothing. And the short codes on small items like sweets are EAN-8, a compact eight-digit format for when there is no room for the full thing.',
      },
      {
        type: 'h2',
        text: 'See it for yourself',
      },
      {
        type: 'p',
        text: 'This is one of the small pleasures of CodePeek. Scan a product barcode and it does not just read the number back at you — it validates the check digit so you know the read was clean, shows the GS1 country or region prefix, and spots when a code is actually an ISBN for a book. It turns an anonymous beep into something you can actually understand. Next time you are unpacking the shopping, point your phone at a label and have a look at what has been hiding on your groceries all along.',
      },
    ],
  },
  {
    slug: 'why-we-build-small-honest-apps',
    title: 'Why we build small, honest apps — and why CodePeek came first',
    date: '2026-07-14',
    author: 'The Teniqua Systems team',
    readingMinutes: 4,
    excerpt:
      'The apps on our phones tend to ask for too much and explain too little. Here is the thinking behind Teniqua Systems, and why our first release is a QR reader that keeps everything on your device.',
    body: [
      {
        type: 'p',
        text: 'A while back one of us was standing at a parking meter that had swapped its coin slot for a sticker with a QR code on it. Scan to pay. The phone camera popped open a link, and there was that half-second of doubt everyone knows: is this the actual parking company, or did someone slap their own sticker over the real one to skim card details? You cannot tell. The link is a jumble of letters, and by the time your browser has loaded the page, you have already gone wherever it wanted to send you.',
      },
      {
        type: 'p',
        text: 'That small moment of not-knowing is the sort of thing we kept bumping into. It is also, more or less, why Teniqua Systems exists.',
      },
      {
        type: 'h2',
        text: 'The problem we kept running into',
      },
      {
        type: 'p',
        text: 'Most of the little utilities we reached for — a QR scanner, a torch, a unit converter — wanted far more than the job required. A flashlight app asking for your contacts. A scanner that uploads every code you point it at to some server so it can show you three ads and a newsletter pop-up. None of it is explained; you just tap "Allow" and hope.',
      },
      {
        type: 'p',
        text: 'We did not want to add another one of those to the pile. So before writing a line of code, we agreed on a handful of rules we would not break.',
      },
      {
        type: 'h2',
        text: 'The rules we set ourselves',
      },
      {
        type: 'p',
        text: 'Do the work on the device wherever it is possible. If a task can happen on your phone, it should never touch a server. Keep each app pointed at one job and let it do that job properly, instead of bolting on features nobody asked for. No dark patterns — no forced sign-ups, no nagging, no quietly selling your data. And when an app does need to carry an ad to stay free, say so in plain language rather than burying it.',
      },
      {
        type: 'p',
        text: 'They sound obvious written down. The interesting part is how many apps break every one of them.',
      },
      {
        type: 'h2',
        text: 'CodePeek: what it actually does',
      },
      {
        type: 'p',
        text: 'CodePeek is our first app, and it grew straight out of that parking-meter moment. Point your camera at a QR code or a barcode — or drop in a screenshot you already have — and it reads the code and shows you what is inside before you act on it.',
      },
      {
        type: 'p',
        text: 'For a link, it pulls the real domain out and puts it front and centre, separate from the rest of the address. A lookalike domain or a padded-out shortened link has nowhere to hide. For the everyday stuff — Wi-Fi logins, contact cards, calendar invites, phone numbers, product barcodes — it parses the fields and lays them out in plain English instead of dumping raw text at you. And it never opens anything for you. It shows you what the code holds and leaves the decision where it belongs: with you.',
      },
      {
        type: 'h2',
        text: 'Why it runs in the browser, and why on-device',
      },
      {
        type: 'p',
        text: 'CodePeek is a web app first. Open the page and it works — no install, no account. If you want it on your home screen you can install it on Android or an iPhone, and from then on it runs offline too, because there is no server it needs to phone home to.',
      },
      {
        type: 'p',
        text: 'That last point is the one we care about most. Every scan is decoded right there in the browser. No image, no decoded text, and no record of what you scanned is ever sent anywhere. We built it that way on purpose: the most private way to handle your data is to never receive it in the first place.',
      },
      {
        type: 'h2',
        text: 'What comes next',
      },
      {
        type: 'p',
        text: 'We work on one thing at a time and finish it before moving on. Next up is Leru, a weather app for people who just want a straight answer about the day ahead — do I need a jacket, is the rain going to hold off — without a screen full of clutter. It is in development now.',
      },
      {
        type: 'p',
        text: 'If you have a small, useful app you wish existed, or you have tried CodePeek and something is not working, tell us. We are a small team, which means a real person reads what you send, and the things people ask for genuinely shape what we build next.',
      },
    ],
  },
];

export function getAllPosts() {
  return [...posts].sort((a, b) => (a.date < b.date ? 1 : -1));
}

export function getPostBySlug(slug) {
  return posts.find((post) => post.slug === slug) || null;
}

export function formatDate(iso) {
  const date = new Date(iso);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export default posts;
